import { AuthenticatorTransportFuture } from "@simplewebauthn/browser";
import { IContract, IFormQuestion, LOGIN_METHOD, PRODUCT_WALKTHROUGH_TYPE } from "../../contract-management/models/contract-management.model";
import { IChecklistItem, IDocumentType, ITag, ITagType } from "../../contract-management/models/settings.model";
import { IKnowledgeBaseStore } from "../../contract-management/services/knowledgebase.service";
import { ADDITIONAL_ROLES, COMMENTS_TYPE, CONTRACT_CHECKLIST_STATUS, SUBSCRIPTION_STATUS, TRIAL_LIFECYCLE_PHASE, USER_ROLE, USER_TYPE } from "./enums";
import { PRIMARY_AUTH_METHOD } from "../services/authentication.service";
import { IPlan, TermUnit } from "src/app/pricing/models";
import { LLM_PROVIDERS } from "src/app/features/general-agent/models/model";

export interface ResponseJson<T> {
    data: T;
    success: boolean;
    message: string;
}


export interface Document {
    _id: string
}

export interface IOrganization extends Document {
    name: string;
    companyId: string;
    office_address: string;
    size: string;
    email: string;
    organizationLogo: string | IAttachment;
    organizationLogoUrl: string;
    company_contact_no: {
        countryCode: string,
        contactNumber: string,
        countryShortName: string
    };
    createdBy: string;
    workspaceSlug: string;
    plan: string
    servicesNeeded: {
        services: string[],
        status: string,
        paymentStatus: string,
    },
    discountCoupons: {
        code: string,
        discount: number,
        isRedeemed: boolean,
        expiryDate: Date
    }[],
    contactedSales: boolean
    active: boolean,
    domain: string,
    loginMethods?: LOGIN_METHOD[]
};

export interface IAttachments extends Document {
    name: string,
    attachment: IAttachment['_id']
}


export interface IAttachment extends Document {
    filename: string;
    url: string;
    type: string;
    createdAt?: Date;
    updatedAt?: Date;
    isPublic: boolean;
    fullUrl?: string;
    relatedTo?: string;
    project?: string,
    superRef?: string | IAttachment
    filePath?: string
    isVersioned?: boolean
}

export enum AIStepItems {
    KPI = 'KPI',
}

export enum IAIStepStatus {
    PENDING = 'PENDING',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED'
}


export interface IAIStep extends Document {
    organization: string | IOrganization,
    user: string | IUser,
    item: string,
    itemType: AIStepItems,
    message: string,
    details: string,
    step: string,
    comparison: string,
    status: IAIStepStatus
}



export enum USER_TYPES {
    INNCIRCLES = 'INNCIRCLES',
    VENDEE_ADMIN = 'VENDEE_ADMIN',
    OWNER = 'OWNER',
    ADMIN = 'ADMIN'
}

export enum USER_AVAILABILITY_STATUS {
    AVAILABLE = 'AVAILABLE',
    BUSY = 'BUSY',
    DO_NOT_DISTURB = 'DO_NOT_DISTURB',
    BE_RIGHT_BACK = 'BE_RIGHT_BACK',
    APPEAR_OFFLINE = 'APPEAR_OFFLINE',
    OUT_OF_OFFICE = 'OUT_OF_OFFICE'
}

export enum SIGNATURE_TYPES {
    ATTACHMENT = 'ATTACHMENT',
    INITIALS = 'INITIALS',
    CANVAS = 'CANVAS',
}

export enum USER_SOURCE {
    'MAIL' = 'MAIL',
    'WEBSITE' = 'WEBSITE',
    'AZURE' = 'AZURE',
}

export enum DEACTIVATION_STATUS {
    'REQUESTED' = 'REQUESTED',
    'DEACTIVATED' = 'DEACTIVATED'
}


export interface IUser extends Document {
    role: USER_ROLE;
    workspaceRole: SHARE_ACCESS;
    organization: IOrganization | string
    currentWorkspace: IWorkspace | string;
    username: string;
    password: string;
    title: string;
    linkedRoster?: string;
    firstName: string;
    lastName: string;
    gender: GENDER;
    employeeId: string;
    phoneNumber: {
        countryCode: string,
        contactNumber: string,
        countryShortName: string
    };
    email: string;
    verified: boolean;
    type: USER_TYPES;
    availabilityStatus: USER_AVAILABILITY_STATUS;
    profilePicture: IAttachment;
    profile: string;
    active: boolean;
    id: string;
    permissions?: string[];
    signature: {
        signType: SIGNATURE_TYPES;
        attachment: IAttachment['_id'];
        initials: string;
        canvas: string;
    };
    token?: string;
    project?: string;
    builder: string;
    // assets?: string[] | IAttachment[];
    profileUrl?: string;
    outlookMailId?: string;
    outlookGuid?: string;
    fullName?: string;
    vendorNumber?: string,
    colorCode: string;
    assets?: { attachment: IAttachment, name: string }[];
    anonymous?: boolean;
    productWalkThrough?: {
        type: PRODUCT_WALKTHROUGH_TYPE,
        status: boolean,
    }[];
    mfaEnabled: boolean,
    mfaSecret: string,
    webAuthnOptions: {
        challenge: string,
    },
    passkey: {
        id: string,
        publicKey: string,
        counter: number,
        transports: AuthenticatorTransportFuture[],
        success?: boolean,
    } | null,
    primaryAuthMethod: PRIMARY_AUTH_METHOD,
    source: USER_SOURCE,
    deactivationStatus: DEACTIVATION_STATUS,
    agentModeSettings?: {
        askMode: boolean;
        agentMode: boolean;
    },
    additionalRoles?: ADDITIONAL_ROLES[],
    planName?: string | null,
}

export interface IWorkspace extends Document {
    _id: string;
    organization: IOrganization | string;
    type: WORKSPACE_TYPE;
    name: string;
    description?: string;
    color: WORKSPACE_COLOR;
    createdBy: string | IUser;
    createdAt: Date;
    updatedAt: Date;
}

export enum GENDER {
    'MALE' = 'MALE',
    'FEMALE' = 'FEMALE',
}

export enum WORKSPACE_COLOR {
    BLUE = 'blue',
    GREEN = 'green',
    PURPLE = 'purple',
    ORANGE = 'orange',
    RED = 'red',
    YELLOW = 'yellow',
    PINK = 'pink',
    GRAY = 'gray'
}

export enum WORKSPACE_ROLE {
    ADMIN = 'ADMIN',
    EDITOR = 'EDITOR',
    COMMENTER = 'COMMENTER',
    VIEWER = 'VIEWER'
}

export enum WORKSPACE_TYPE {
    DEFAULT = 'DEFAULT',
    CUSTOM = 'CUSTOM'
}

export enum CONTRACT_USER_ROLE {
    OWNER = 'OWNER',
    EDITOR = 'Editor',
    VIEWER = 'Viewer',
    COMMENTER = 'Commenter',
    ADMIN = 'Admin'
}

export enum PROJECT_USER_ROLE {
    OWNER = 'OWNER',
    MANAGER = 'Manager',
    EDITOR = 'Editor',
    VIEWER = 'Viewer',
    COMMENTER = 'Commenter'
}

export enum REQUEST_ACCESS_STATUS {
    PENDING = 'PENDING',
    REQUESTED = 'REQUESTED',
    INVITED = 'INVITED',
}

export enum REQUEST_TYPE {
    CREATION = 'CREATION',
    DEACTIVATION = 'DEACTIVATION',
    CREDITS = "CREDITS",
    PLAN_CHANGE = "PLAN_CHANGE",
    INVITE_PAYMENT = "INVITE_PAYMENT"
}
export interface IRequestAccess extends Document {
    _id: string,
    name: string,
    email: string,
    role: SHARE_ACCESS,
    organization: string | IOrganization,
    workspace: string | IWorkspace,
    requestedBy: string | IUser,
    status: REQUEST_ACCESS_STATUS,
    approvedBy: string | IUser,
    approvedAt: Date,
    createdAt: Date,
    updatedAt: Date,
    requestType: REQUEST_TYPE,
    metadata: Record<string, any>
}

export interface IInvitation extends Document {
    _id: string,
    email: string,
    role: USER_ROLE,
    organization: string,
    invitedBy: string,
    createdAt: Date,
    updatedAt: Date,
}

export enum APPROVER_TYPE {
    INTERNAL = 'INTERNAL',
    EXTERNAL = 'EXTERNAL'
}

export enum SHARE_FEATURE {
    CONTRACT = 'Contract',
    PROJECT = 'Project',
    WORKSPACE = 'Workspace',
    GENERAL_AGENT = 'Thread',
    GEOTECHNICAL_ANALYSIS = 'GeotechnicalAnalysis',
    PROMPT = 'Prompt',
}

export interface IShareUser {
    _id?: string,
    user?: string | IUser,
    email: string,
    name: string,
    status?: string,
    role?: SHARE_ACCESS | SHARE_ACCESS[],
    type: USER_TYPE,
    modifiedAt?: Date,
    reviewed?: boolean
}

export enum SHARE_ACCESS {
    VIEW = 'View',
    EDIT = 'Edit',
    COMMENT = 'Comment',
    RESTRICTED = 'Restricted',
    ADMIN = 'Admin',
    OWNER = 'Owner',
    ORG_ADMIN = 'ADMIN',
    APPROVER = 'Approver',
    SIGNATORY = 'Signatory'
    // MANAGER = 'Manager' // Only for projects 
}

export interface IShareContract extends Document {
    organization: string,
    contract: string | IContract,
    users: IShareUser[],
    anyone: SHARE_ACCESS,
    workspaceShare: boolean,
    projectShare: boolean,
    createdAt: Date,
    updatedAt: Date,
}
export interface IContractChecklistItem extends Document {
    organization: string,
    contract: string | IContract,
    checklistItem: string | IChecklistItem,
    status: CONTRACT_CHECKLIST_STATUS,
    impactRule?: { rule: string, impact: number },
    source?: [{ source: string, recommended: string, sourceDocument: string }],
}

export interface ILinkDocument extends Document {
    sourceDocument?: IContract,
    targetDocument: IContract,
    linkedBy: IUser,
    createdAt?: Date
}

export interface IAdobeSignSetting extends Document {
    clientId: string
    clientSecret: string
    organization: string | IOrganization,
    consent: boolean
}

export enum PlanType {
    DEFAULT = 'DEFAULT',
    CUSTOM = 'CUSTOM',
}

export enum PlanFeature {
    AGENT = 'AGENT',
    INN_DOC = 'INN_DOC',
}

export enum PlanStatus {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    SUSPENDED = 'SUSPENDED',
    EXPIRED = 'EXPIRED',
}

export enum PaymentStatus {
    PENDING = 'PENDING',
    PAID = 'PAID',
    FAILED = 'FAILED',
    REFUNDED = 'REFUNDED',
    CANCELLED = 'CANCELLED',
}
export enum BILLING_CYCLE {
    MONTHLY = 'monthly',
    YEARLY = 'yearly',
}

export enum PAYMENT_STATUS {
    UNPAID = "UNPAID",       // Trial period, no payment made
    PENDING = "PENDING",     // Payment in progress
    PAID = "PAID",          // Payment completed successfully
    FAILED = "FAILED",       // Payment failed
    UNSUBSCRIBED = "UNSUBSCRIBED" // User has unsubscribed or subscription ended
}

export enum PAYMENT_MODE {
    STRIPE = 'STRIPE',
    SALES = 'SALES' // Manually marked as paid via sales portal, no Stripe subscription
}


export interface ISubscriptionDetails extends Document {
    organization: string | IOrganization,
    status: SUBSCRIPTION_STATUS,
    plan: string | IPlan,

    // Credits allocation config
    creditsConfig?: {
        max_allocation: number;
        max_org_allocation: number;
    };

    // Payment Status
    paymentStatus: PAYMENT_STATUS,  // Payment status indicator
    paymentMode?: PAYMENT_MODE,        // e.g. 'STRIPE', 'SALES'
    manualOverrideExpiryDate?: Date,  // Manual override expiry date; organization needs Stripe subscription after this

    // Payment Details
    paidDate: Date,
    subscriptionId: string,
    customerId: string,

    // Additional payment tracking fields
    amountPaid?: number,           // Actual amount charged
    currency?: string,             // Payment currency
    paymentMethod?: string,        // Payment method used
    billingCycle?: BILLING_CYCLE,         // Monthly/Yearly
    billing?: {
        price: number;
        term: TermUnit;
        askBudget: number;
        agentBudget: number;
        rolloverPercentage: number;
        rolloverCap: number;
    };
    nextBillingDate?: Date,        // When next payment is due
    lastPaymentAmount?: number,    // Last payment amount
    totalPaid?: number,           // Total amount paid over time

    // AI providers enabled on this subscription (plan-level setting, outside billing snapshot)
    modelAccess?: LLM_PROVIDERS[];

    // Trial fields
    trialStartDate?: Date,         // When trial started
    trialEndDate?: Date,           // When trial ends
    trial: {
        enabled: boolean;
        duration: number;
        term: TermUnit;
    };       // Trial duration in days
    trialLifecyclePhase?: TRIAL_LIFECYCLE_PHASE; // Current phase after trial ends
    trialExpiryProcessed: boolean;
    cancelPendingAtPeriodEnd?: boolean;
    cancelAtPeriodEndDate?: Date;
    pendingDowngrade?: {
        scheduledChangeId: string;
        targetPlan: { _id: string; name: string };
        effectiveAt: Date;
        requestedAt?: Date;
    };

    subscriptionHistory?: {
        subscriptionId: string;
        planId: string;
        planName: string;
        status: SUBSCRIPTION_STATUS;
        source?: string;
        createdAt: Date;
        cancelledAt?: Date;
        cancelReason?: string;
    }[];

    topUps?: {
        sessionId: string;
        amount: number;
        currency: string;
        paymentMethod?: string;
        creditSourceType: string;
        paidAt: Date;
        metadata: Record<string, string | number | boolean | string[]>;
    }[];
}


export interface IDocumentVersion extends Document {
    contract: string | IContract,
    createdBy: IUser,
    file: IAttachment,
    name: string,
    version: string,
    updatedAt: Date,
    createdAt: Date
}


export interface IKnowledgeMapping extends Document {
    _id: string;
    organization: string | IOrganization;
    documentType: string | IDocumentType;
    linkedDocument: string | IFile | (IKnowledgeBaseStore & { size?: number });
    linkedDocumentType: LINKED_DOCUMENT_TYPE;
    createdAt: Date;
    createdBy: string | IUser;
}

export enum LINKED_DOCUMENT_TYPE {
    FILE = 'File',
    KNOWLEDGE_BASE_STORE = 'KnowledgeBaseStore'
}

export enum PROJECT_STATUS {
    OPEN = 'OPEN',
    COMPLETED = 'COMPLETED',
    OVERDUE = 'OVERDUE',
    ARCHIVED = 'ARCHIVED'
}


export enum STATUS_TYPE {
    PROJECT = 'PROJECT',
    DOCUMENT = 'DOCUMENT',
}

export interface IStatus extends Document {
    statusType: STATUS_TYPE;
    name: string;
    organization: string | IOrganization;
    documentType?: string | IDocumentType;
    standardStatus: string | null;
    active: boolean;
    createdAt: Date;
    updatedAt: Date;
}


export interface ICustomer extends Document {
    _id: string;
    name: string;
    organization: string | IOrganization;
    createdAt: Date;
    updatedAt: Date;
}

export interface IProjectTag extends Document {
    tagTypeId: string | ITagType;
    value: string | string[];
}

export interface IProject extends Document {
    organization: string | IOrganization;
    workspace: string | IWorkspace;
    projectId: string;
    name: string;
    status: IStatus | string;
    managers: (string | IUser)[];
    customer: string | ICustomer | null;
    dueDate: Date;
    internal: boolean;
    progress: number;
    active: boolean;
    createdAt: Date;
    updatedAt: Date;
    createdBy?: string | IUser;
    tag?: IProjectTag[];
}

export interface IDocumentMetrics {
    openDocumentIds: string[],
    closedDocumentIds: string[],
    upcomingDeadlineDocumentIds: string[],
    overdueDocumentIds: string[]
}

export interface IComment extends Document {
    organization: string | IOrganization,
    user: IUser
    comment: string
    itemId: string,
    docModel: COMMENTS_TYPE,
    mentions?: string[] | IUser[]
    createdAt: Date,
    updatedAt: Date
}

export enum MILESTONE_TYPE {
    PROJECT = 'PROJECT',
    DOCUMENT = 'DOCUMENT',
}

export enum DAY_OF_WEEK {
    MON = 'mon',
    TUE = 'tue',
    WED = 'wed',
    THU = 'thu',
    FRI = 'fri',
    SAT = 'sat',
    SUN = 'sun',
}

export interface IReminder extends Document {
    organization: string | IOrganization;
    milestone: string | IMilestone;
    date: Date;
    quickOption: number; // 1: 1 day before, 7: 1 week before, 30: 1 month before, 365: 1 year before
    time: Date;
    repeat: {
        enabled: boolean;
        option: 'none' | 'daily' | 'weekly' | 'weekdays' | 'monthly' | 'yearly' | 'custom';
        unit?: 'hour' | 'day' | 'week' | 'month' | 'year';
        weekDays?: DAY_OF_WEEK[];
        frequency?: number;
    }
    end: {
        value: 'never' | 'on' | 'after';
        date?: Date;
        occurrences?: number;
    }
    deliveryChannels: {
        inApp: boolean;
        email: boolean;
    }
    createdAt: Date;
    updatedAt: Date;
    createdBy: string | IUser;
}

export interface IMilestone {
    _id?: string;
    type: MILESTONE_TYPE;
    name: string;
    reminder: IReminder;
    date: Date;
    standardMilestone: string | null;
    notifyDayPrior?: boolean;
    notifyOn: string;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface ISubscriptionResponse {
    plan: IPlan;
    subscriptions: ISubscriptionDetails;
    category?: string;
}

export enum FILE_TYPE {
    'FOLDER' = 'FOLDER',
    'FILE' = 'FILE',
    'ZIP' = 'application/zip'
}

export enum FILE_EXTENSION {
    'DOCX' = 'docx',
    'PDF' = 'pdf',
}

export interface IDocumentKnowledge {
    _id: string,
    item: string,
    docModel: string,
    markdown: string,
    embeddingsStatus: string,
    createdBy: string,
    createdAt: Date,
    updatedAt: Date,
}

export enum ATTACHMENT_SOURCE {
    UPLOADED_FILES = 'UPLOADED_FILES',
    PROJECT_DOCUMENTS = 'PROJECT_DOCUMENTS',
    KNOWLEDGE_BASE_FILES = 'KNOWLEDGE_BASE_FILES',
    GOOGLE_DRIVE_FILES = 'GOOGLE_DRIVE_FILES',
}

export interface IFile {
    readonly _id: string,
    organization: string,
    attachment: string | IAttachment,
    name: string,
    type: FILE_TYPE,
    root: string | null,
    parent: string | null,
    url: string,
    size: number,
    count: number,
    mimeType?: FILE_EXTENSION,
    documentType: IDocumentType,
    reviewed: boolean,
    createdAt: Date,
    updatedAt: Date,
    createdBy: IUser
    documentKnowledge?: IDocumentKnowledge,
    knowledgeBases?: IKnowledgeBaseStore[],
    numberOfTimesUsed?: number,
    source?: ATTACHMENT_SOURCE,
    project?: string | Pick<IProject, '_id' | 'name'>
}

export type ExtractedEntity = {
    entity_id: string
    docTypeId: string
    fileId: string
    entity_name: string
    extracted_entity: string
    reason: string
}

export interface SupportTicketAttachment {
    file: File;
    preview: string;
    isImage: boolean;
    isVideo: boolean;
}

export interface OpenSupportTicketDialogPayload {
    attachment?: SupportTicketAttachment;
}

export interface CreateTicketPayload {
    name: string;
    company: string;
    email: string;
    title: string;
    description: string;
    customerId: string;
    organizationId: string;
}

export interface UploadAttachmentsPayload {
    issueKey: string;
    files: File[];
}

export interface RecordingState {
    isRecording: boolean;
    recordingTime: number;
    mediaRecorder: MediaRecorder | null;
    recordedChunks: Blob[];
}

export interface RecordingCompleted {
    blob: Blob;
    videoUrl: string;
    file: File;
}

export interface CaptureResult {
    file: File;
    preview: string;
    isImage: boolean;
    isVideo: boolean;
}

